const MAX_FILE_BYTES = 15 * 1024 * 1024
const MAX_TOTAL_BYTES = 100 * 1024 * 1024
const encoder = new TextEncoder()

export function imageExtension(bytes) {
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpg'
  if ([137, 80, 78, 71, 13, 10, 26, 10].every((v, i) => bytes[i] === v)) return 'png'
  throw new Error('Report photos must be JPEG or PNG. Convert unsupported images before exporting.')
}

function sourcesFor(tx) {
  const sources = []
  const warnings = []
  const captures = tx.captures || {}
  if (captures.id_front) sources.push({ role: 'customerId', key: 'id_front', source: captures.id_front })
  if (captures.customer_photo) sources.push({ role: 'customer', key: 'customer_photo', source: captures.customer_photo })
  ;(tx.items || []).forEach((item, itemIndex) => {
    const key = `item_photo_${itemIndex}`
    const metadata = tx.captureMetadata?.[key]
    if (metadata?.itemId && item.itemId && String(metadata.itemId) !== String(item.itemId)) {
      throw new Error(`Purchase ${tx.transactionNumber || tx.id}: item photo association needs review.`)
    }
    if (captures[key]) sources.push({ role: 'item', key, itemIndex, source: captures[key] })
  })
  // Migrated records have no guaranteed photo-to-item relationship. Use only
  // unambiguous categories, never guess from gallery ordering or client names.
  const legacy = (tx.files || []).filter(f => f?.path)
  const idPhotos = legacy.filter(f => ['id_photo', 'id_front'].includes(f.fileType))
  const itemPhotos = legacy.filter(f => ['item_photo', 'item'].includes(f.fileType))
  const portraits = legacy.filter(f => ['customer_photo'].includes(f.fileType))
  if (!captures.id_front && idPhotos.length === 1) sources.push({ role: 'customerId', key: 'id_front', source: idPhotos[0].path })
  if (!captures.customer_photo && portraits.length === 1) sources.push({ role: 'customer', key: 'customer_photo', source: portraits[0].path })
  if (tx.items?.length === 1 && !captures.item_photo_0 && itemPhotos.length === 1) {
    sources.push({ role: 'item', key: 'item_photo_0', itemIndex: 0, source: itemPhotos[0].path })
  } else if (itemPhotos.length && !sources.some(s => s.role === 'item')) {
    warnings.push({ transactionId: tx.id, message: 'Legacy item photos were omitted: their association with individual items needs review.' })
  }
  if (idPhotos.length > 1 || portraits.length > 1) warnings.push({ transactionId: tx.id, message: 'Multiple legacy ID/customer photos need review; no arbitrary image was selected.' })
  return { sources, warnings }
}

/** Explicit opt-in only. downloadBlob is injected; this module never contacts a server. */
export async function prepareRissAttachments(transactions, downloadBlob) {
  const attachmentFilenames = Object.create(null)
  const files = []
  const warnings = []
  let totalBytes = 0
  const seen = new Set()
  for (let index = 0; index < transactions.length; index++) {
    const tx = transactions[index]
    if (!tx.id || seen.has(String(tx.id))) throw new Error('Attachment export requires unique purchase IDs.')
    seen.add(String(tx.id))
    const mapping = { item: {} }
    attachmentFilenames[tx.id] = mapping
    const selected = sourcesFor(tx)
    warnings.push(...selected.warnings)
    for (const entry of selected.sources) {
      const blob = await downloadBlob(entry.source, { transactionId: String(tx.id), captureKey: entry.key })
      if (!blob?.size || blob.size > MAX_FILE_BYTES || totalBytes + blob.size > MAX_TOTAL_BYTES) {
        throw new Error('Photo export is limited to 15 MB per image and 100 MB total. Use a smaller date range.')
      }
      const bytes = new Uint8Array(await blob.arrayBuffer())
      const ext = imageExtension(bytes)
      // Deliberately no customer name, ID number, storage token or path.
      const name = `purchase-${String(index + 1).padStart(5, '0')}-${entry.key}.${ext}`
      files.push({ transactionId: String(tx.id), name, bytes })
      totalBytes += bytes.length
      if (entry.role === 'item') mapping.item[entry.itemIndex] = name
      else mapping[entry.role] = name
    }
  }
  return { attachmentFilenames, files, warnings }
}

export function crc32(bytes) {
  let crc = 0xffffffff
  for (const byte of bytes) {
    crc ^= byte
    for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0)
  }
  return (crc ^ 0xffffffff) >>> 0
}

/** Small, standard STORE-mode ZIP writer; no dependencies, compression or ZIP64. */
export function createRissZip(entries) {
  if (!entries.length || entries.length > 65535) throw new Error('Invalid ZIP file count.')
  const parts = []
  const central = []
  const names = new Set()
  let offset = 0
  for (const entry of entries) {
    const name = entry.name
    if (typeof name !== 'string' || !/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(name) || name.includes('..') || names.has(name.toLowerCase())) {
      throw new Error('Unsafe or duplicate ZIP filename.')
    }
    names.add(name.toLowerCase())
    const nameBytes = encoder.encode(name)
    const bytes = typeof entry.bytes === 'string' ? encoder.encode(entry.bytes) : entry.bytes
    if (!(bytes instanceof Uint8Array) || bytes.length > MAX_TOTAL_BYTES || nameBytes.length > 65535) throw new Error('Invalid ZIP entry.')
    const crc = crc32(bytes)
    const local = new Uint8Array(30 + nameBytes.length)
    const lv = new DataView(local.buffer)
    lv.setUint32(0, 0x04034b50, true)
    lv.setUint16(4, 20, true)
    lv.setUint16(6, 0x0800, true)
    lv.setUint16(12, 33, true) // DOS 1980-01-01, deterministic metadata.
    lv.setUint32(14, crc, true)
    lv.setUint32(18, bytes.length, true)
    lv.setUint32(22, bytes.length, true)
    lv.setUint16(26, nameBytes.length, true)
    local.set(nameBytes, 30)
    const directory = new Uint8Array(46 + nameBytes.length)
    const dv = new DataView(directory.buffer)
    dv.setUint32(0, 0x02014b50, true)
    dv.setUint16(4, 20, true)
    dv.setUint16(6, 20, true)
    dv.setUint16(8, 0x0800, true)
    dv.setUint16(14, 33, true)
    dv.setUint32(16, crc, true)
    dv.setUint32(20, bytes.length, true)
    dv.setUint32(24, bytes.length, true)
    dv.setUint16(28, nameBytes.length, true)
    dv.setUint32(42, offset, true)
    directory.set(nameBytes, 46)
    parts.push(local, bytes)
    central.push(directory)
    offset += local.length + bytes.length
    if (offset > MAX_TOTAL_BYTES + 5 * 1024 * 1024) throw new Error('ZIP exceeds the export size limit.')
  }
  const centralSize = central.reduce((sum, p) => sum + p.length, 0)
  const end = new Uint8Array(22)
  const ev = new DataView(end.buffer)
  ev.setUint32(0, 0x06054b50, true)
  ev.setUint16(8, entries.length, true)
  ev.setUint16(10, entries.length, true)
  ev.setUint32(12, centralSize, true)
  ev.setUint32(16, offset, true)
  const all = [...parts, ...central, end]
  const result = new Uint8Array(offset + centralSize + end.length)
  let cursor = 0
  for (const part of all) { result.set(part, cursor); cursor += part.length }
  return result
}
